import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { PLANS, getPlanPriceDisplay } from "../config/plans";
import {
  parseRefParams,
  saveParamsToSession,
  trackVisit,
  trackFormStart,
  trackCompletion,
  loadParamsFromSession,
  getOrCreateSessionId,
} from "../utils/registrationTracker";
import {
  Mail,
  Lock,
  User,
  Building2,
  AlertCircle,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";

const IS_SAAS = process.env.REACT_APP_IS_SAAS === "true";

function Register() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "",
    surname: "",
    businessName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, refreshToken } = useAuth();
  const { t, i18n } = useTranslation('auth');

  useEffect(() => {
    if (!IS_SAAS) return;
    const params = parseRefParams(searchParams.toString());
    saveParamsToSession(params);
    trackVisit(params);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!IS_SAAS) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <p className="text-gray-500">{t('register.saasOnly')}</p>
          <Link to="/login" className="mt-4 inline-block text-blue-600 hover:underline">
            {t('register.goToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleStep1 = (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) {
      setError(t('register.errors.passwordMismatch'));
      return;
    }
    if (form.password.length < 8) {
      setError(t('register.errors.passwordTooShort'));
      return;
    }
    if (!form.businessName.trim()) {
      setError(t('register.errors.businessNameRequired'));
      return;
    }
    trackFormStart();
    setStep(2);
  };

  const handleSubmit = async () => {
    setError("");
    setLoading(true);
    try {
      await register(form.email, form.password);
      const setupBusinessFn = httpsCallable(functions, "setupBusiness");
      // URL parametreleri (adım 2'de URL hâlâ geçerli) öncelikli; fallback sessionStorage
      const urlParams = parseRefParams(searchParams.toString());
      const sessionParams = loadParamsFromSession();
      const hasUrlParams = !!(urlParams.ref || urlParams.utmSource || urlParams.utmCampaign);
      const sourceParams = hasUrlParams ? urlParams : sessionParams;
      const sessionId = getOrCreateSessionId();
      const registrationSource = Object.values(sourceParams).some(v => v)
        ? { ...sourceParams, sessionId }
        : null;
      const result = await setupBusinessFn({
        businessName: form.businessName.trim(),
        name: form.name.trim(),
        surname: form.surname.trim(),
        planId: selectedPlan,
        billingCycle,
        registrationSource,
      });
      await trackCompletion(result?.data?.businessId || null);
      await refreshToken();
      navigate("/home", { replace: true });
    } catch (err) {
      console.error("Kayıt hatası:", err);
      if (err.code === "auth/email-already-in-use") {
        setError(t('register.errors.emailAlreadyInUse'));
      } else if (err.code === "functions/unauthenticated") {
        setError(t('register.errors.authError'));
      } else {
        setError(err.message || t('register.errors.generalError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100 transition-all duration-200"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        {t('register.backToLogin')}
      </button>
      <div className="fixed top-4 right-4">
        <LanguageSwitcher />
      </div>

      <div className={`mx-auto ${step === 2 ? 'max-w-3xl' : 'max-w-md'}`}>
        {/* Başlık */}
        <div className="text-center mb-6">
          <div className="w-full h-12 mb-4 flex justify-center">
            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-2xl font-bold py-2 px-4 rounded-lg">
              Adisyon Takip
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            {step === 1 ? t('register.step1Title') : t('register.step2Title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {step === 1
              ? t('register.step1Subtitle')
              : t('register.step2Subtitle')}
          </p>
          {/* Adım göstergesi */}
          <div className="flex items-center justify-center gap-2 mt-3">
            {[1, 2].map((s) => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all ${s === step ? 'w-8 bg-blue-500' : s < step ? 'w-4 bg-blue-300' : 'w-4 bg-gray-200'}`}
              />
            ))}
          </div>
        </div>

        {/* Hata Mesajı */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md mb-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* ─── ADIM 1: İşletme Bilgileri ─── */}
        {step === 1 && (
          <form onSubmit={handleStep1} className="space-y-4 bg-white rounded-xl border border-gray-200 p-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.businessNameLabel')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="businessName"
                  required
                  value={form.businessName}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('register.businessNamePlaceholder')}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:fields.name')}</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('common:fields.name')}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:fields.surname')}</label>
                <input
                  type="text"
                  name="surname"
                  value={form.surname}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('common:fields.surname')}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('login.emailLabel')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ornek@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('login.passwordLabel')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  name="password"
                  required
                  value={form.password}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('register.passwordPlaceholder')}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.confirmPasswordLabel')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  name="confirmPassword"
                  required
                  value={form.confirmPassword}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('register.confirmPasswordPlaceholder')}
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full flex justify-center items-center py-2.5 px-4 text-sm font-medium rounded-md text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 transition-all duration-300"
            >
              {t('register.continueButton')}
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </form>
        )}

        {/* ─── ADIM 2: Plan Seçimi ─── */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Aylık / Yıllık Toggle */}
            <div className="flex items-center justify-center gap-3">
              <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>
                {t('register.monthlyLabel')}
              </span>
              <button
                type="button"
                onClick={() => setBillingCycle((prev) => prev === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${billingCycle === 'yearly' ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-400'}`}>
                {t('register.yearlyLabel')}
                <span className={`ml-1.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${billingCycle === 'yearly' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                  {t('register.savings17')}
                </span>
              </span>
            </div>

            {/* Plan Kartları */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {PLANS.map((plan) => {
                const priceDisplay = getPlanPriceDisplay(plan.id, billingCycle, i18n.language);
                const isSelected = selectedPlan === plan.id;
                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative text-left rounded-xl border-2 p-5 transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {plan.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[11px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                        {t('register.mostPopular')}
                      </span>
                    )}
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-bold text-gray-900">{plan.name}</h3>
                      {isSelected && <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-gray-500 mb-3">{plan.description}</p>
                    <div className="flex items-baseline gap-1 mb-3">
                      <span className="text-2xl font-bold text-gray-900">
                        {priceDisplay.symbolBefore ? `${priceDisplay.symbol}${priceDisplay.formatted}` : priceDisplay.formatted}
                      </span>
                      {!priceDisplay.symbolBefore && (
                        <span className="text-gray-500 text-sm">{priceDisplay.symbol}</span>
                      )}
                      <span className="text-gray-400 text-xs">{billingCycle === 'yearly' ? t('register.yearSuffix') : t('register.monthSuffix')}</span>
                    </div>
                    {billingCycle === 'yearly' && (
                      <p className="text-xs text-green-600 mb-2">
                        {t('register.perMonthEquivalent', {
                          amount: priceDisplay.symbolBefore
                            ? `${priceDisplay.symbol}${priceDisplay.yearlyMonthlyEquivFormatted}`
                            : `${priceDisplay.yearlyMonthlyEquivFormatted} ${priceDisplay.symbol}`,
                        })}
                      </p>
                    )}
                    <ul className="space-y-1.5">
                      {plan.highlights.map((h) => (
                        <li key={h} className="flex items-start gap-1.5 text-xs text-gray-600">
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                          {h}
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>

            {/* Bilgi Notu */}
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
              <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">
                {t('register.trialNote')}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setStep(1); setError(''); }}
                className="flex-1 py-2.5 px-4 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('register.backButton')}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-[2] flex justify-center items-center py-2.5 px-4 text-sm font-medium rounded-md text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('register.creatingAccount')}
                  </>
                ) : (
                  t('register.startTrialButton')
                )}
              </button>
            </div>
          </div>
        )}

        <p className="text-center text-sm text-gray-500 mt-4">
          {t('register.alreadyHaveAccount')}{' '}
          <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
            {t('register.loginLink')}
          </Link>
        </p>

        <p className="text-center text-sm text-gray-400 mt-2">
          {t('register.copyright')}
        </p>
      </div>
    </div>
  );
}

export default Register;
